import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiUpload, FiDollarSign, FiMapPin } from 'react-icons/fi';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };
};

function CompanySettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    company_address: '',
    company_city_state_zip: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    company_logo: '',
    invoice_prefix: 'INV-',
    next_invoice_number: 1001,
    quote_prefix: 'QTE-',
    next_quote_number: 1001,
    default_terms: 'Net 30',
    default_notes: '',
    // Job Costing
    office_address: '',
    office_lat: null,
    office_lng: null,
    mileage_rate: 0.67,
    overhead_rate_per_hour: 0,
    rebar_cost: 0,
    pipe_cost: 0,
    // Overhead Breakdown
    overhead_vehicle_payments: 0,
    overhead_fuel: 0,
    overhead_vehicle_maintenance: 0,
    overhead_vehicle_insurance: 0,
    overhead_general_liability: 0,
    overhead_workers_comp: 0,
    overhead_professional_liability: 0,
    overhead_equipment_payments: 0,
    overhead_software: 0,
    overhead_equipment_maintenance: 0,
    overhead_office_rent: 0,
    overhead_utilities: 0,
    overhead_phone_internet: 0,
    overhead_office_supplies: 0,
    overhead_owner_draw: 0,
    overhead_admin_staff: 0,
    overhead_payroll_taxes: 0,
    overhead_health_insurance: 0,
    overhead_accounting: 0,
    overhead_legal: 0,
    overhead_licensing: 0,
    overhead_marketing: 0,
    overhead_other: 0,
    overhead_other_note: '',
    overhead_annual_billable_hours: 4000
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const config = await getAuthConfig();
      const response = await axios.get(`${API_URL}/company-settings`, config);
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading settings:', error);
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo file must be less than 2MB');
      return;
    }
    
    setUploading(true);
    
    try {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await axios.post(`${API_URL}/company-settings/logo`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });
      
      setSettings(prev => ({ ...prev, company_logo: response.data.logo_url }));
      setUploading(false);
    } catch (error) {
      console.error('Error uploading logo:', error);
      alert('Failed to upload logo');
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Remove company logo?')) return;
    
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/company-settings/logo`, config);
      setSettings(prev => ({ ...prev, company_logo: '' }));
    } catch (error) {
      console.error('Error removing logo:', error);
      alert('Failed to remove logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const config = await getAuthConfig();
      await axios.put(`${API_URL}/company-settings`, settings, config);
      alert('Settings saved successfully!');
      setSaving(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>Loading settings...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <button
          onClick={() => navigate('/billing')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            marginBottom: '16px',
            padding: '0'
          }}
        >
          <FiArrowLeft /> Back to Billing
        </button>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>Company Settings</h1>
        <p style={{ color: '#6b7280', marginTop: '8px' }}>
          Configure your company information for invoices
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Company Logo */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Company Logo
          </h2>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            {/* Logo Preview */}
            <div style={{
              width: '150px',
              height: '150px',
              border: '2px dashed #d1d5db',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f9fafb',
              overflow: 'hidden'
            }}>
              {settings.company_logo ? (
                <img 
                  src={`${API_BASE_URL}${settings.company_logo}`}
                  alt="Company Logo"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#9ca3af' }}>
                  <FiUpload size={32} style={{ marginBottom: '8px' }} />
                  <p style={{ margin: 0, fontSize: '12px' }}>No logo</p>
                </div>
              )}
            </div>
            
            {/* Upload Controls */}
            <div>
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6b7280' }}>
                Upload your company logo to display on invoices.<br />
                Recommended size: 300x150 pixels. Max 2MB.
              </p>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  borderRadius: '8px',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: uploading ? 0.7 : 1
                }}>
                  <FiUpload size={16} />
                  {uploading ? 'Uploading...' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>
                
                {settings.company_logo && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Company Information */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Company Information
          </h2>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Company Name
            </label>
            <input
              type="text"
              value={settings.company_name}
              onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Street Address
            </label>
            <input
              type="text"
              value={settings.company_address}
              onChange={(e) => setSettings(prev => ({ ...prev, company_address: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              City, State, ZIP
            </label>
            <input
              type="text"
              value={settings.company_city_state_zip}
              onChange={(e) => setSettings(prev => ({ ...prev, company_city_state_zip: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Phone
              </label>
              <input
                type="tel"
                value={settings.company_phone}
                onChange={(e) => setSettings(prev => ({ ...prev, company_phone: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Email
              </label>
              <input
                type="email"
                value={settings.company_email}
                onChange={(e) => setSettings(prev => ({ ...prev, company_email: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Website
            </label>
            <input
              type="url"
              value={settings.company_website}
              onChange={(e) => setSettings(prev => ({ ...prev, company_website: e.target.value }))}
              placeholder="https://www.example.com"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        {/* Invoice Settings */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Invoice Settings
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Invoice Number Prefix
              </label>
              <input
                type="text"
                value={settings.invoice_prefix}
                onChange={(e) => setSettings(prev => ({ ...prev, invoice_prefix: e.target.value }))}
                placeholder="INV-"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Next Invoice Number
              </label>
              <input
                type="number"
                value={settings.next_invoice_number}
                onChange={(e) => setSettings(prev => ({ ...prev, next_invoice_number: parseInt(e.target.value) || 1 }))}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          {/* Quote Numbering */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Quote Prefix
              </label>
              <input
                type="text"
                value={settings.quote_prefix}
                onChange={(e) => setSettings(prev => ({ ...prev, quote_prefix: e.target.value }))}
                placeholder="QTE-"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
                Next Quote Number
              </label>
              <input
                type="number"
                value={settings.next_quote_number}
                onChange={(e) => setSettings(prev => ({ ...prev, next_quote_number: parseInt(e.target.value) || 1 }))}
                min="1"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Default Payment Terms
            </label>
            <select
              value={settings.default_terms}
              onChange={(e) => setSettings(prev => ({ ...prev, default_terms: e.target.value }))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="Due on Receipt">Due on Receipt</option>
              <option value="Net 15">Net 15</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 45">Net 45</option>
              <option value="Net 60">Net 60</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px' }}>
              Default Invoice Notes
            </label>
            <textarea
              value={settings.default_notes}
              onChange={(e) => setSettings(prev => ({ ...prev, default_notes: e.target.value }))}
              rows="3"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                resize: 'vertical'
              }}
              placeholder="Thank you for your business!"
            />
            <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px' }}>
              This text will appear at the bottom of all new invoices
            </p>
          </div>
        </div>

        {/* Notifications */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 16px', fontSize: '18px', color: '#374151' }}>Notifications</h2>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={!!settings.notify_quote_requests}
              onChange={(e) => setSettings(prev => ({ ...prev, notify_quote_requests: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              Notify owners/admins about new quote requests
            </span>
          </label>
          <p style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', marginLeft: '28px' }}>
            When off, new quote requests won't create in-app notifications — they'll still show in the Quote Requests list. Job notifications (assignments, reviews, etc.) are unaffected.
          </p>
        </div>

        {/* Preview */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ margin: '0 0 20px', fontSize: '18px', color: '#374151' }}>
            Invoice Header Preview
          </h2>
          
          <div style={{
            padding: '24px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1a1a1a', marginBottom: '8px' }}>
                  {settings.company_name || 'Your Company Name'}
                </div>
                <div style={{ color: '#666', fontSize: '13px', lineHeight: '1.6' }}>
                  {settings.company_address && <div>{settings.company_address}</div>}
                  {settings.company_city_state_zip && <div>{settings.company_city_state_zip}</div>}
                  {settings.company_phone && <div>{settings.company_phone}</div>}
                  {settings.company_email && <div>{settings.company_email}</div>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb' }}>INVOICE</div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {settings.invoice_prefix}{settings.next_invoice_number}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#16a34a', marginTop: '16px' }}>QUOTE</div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {settings.quote_prefix}{settings.next_quote_number}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Job Costing Settings */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          <h2 style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <FiDollarSign style={{ color: '#16a34a' }} /> Job Costing Settings
          </h2>
          
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '14px' }}>
            Configure rates and costs for job profitability calculations. These settings help track expenses and determine profit margins on each job.
          </p>

          {/* Office Location for Mileage */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiMapPin /> Office Location (for mileage calculation)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Office Address
                </label>
                <input
                  type="text"
                  value={settings.office_address || ''}
                  onChange={(e) => setSettings({...settings, office_address: e.target.value})}
                  placeholder="292 Erwin Rd S.E., Adairsville, GA"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Office Latitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={settings.office_lat || ''}
                  onChange={(e) => setSettings({...settings, office_lat: parseFloat(e.target.value) || null})}
                  placeholder="34.3684"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Office Longitude
                </label>
                <input
                  type="number"
                  step="0.000001"
                  value={settings.office_lng || ''}
                  onChange={(e) => setSettings({...settings, office_lng: parseFloat(e.target.value) || null})}
                  placeholder="-84.9382"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              💡 You can find coordinates by searching your address on Google Maps and copying the lat/lng from the URL.
            </p>
          </div>

          {/* Rates */}
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Mileage Rate</h3>
            <div style={{ maxWidth: '250px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                Mileage Rate ($/mile)
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.mileage_rate || ''}
                onChange={(e) => setSettings({...settings, mileage_rate: parseFloat(e.target.value) || 0})}
                placeholder="0.67"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>2024 IRS rate: $0.67/mile</p>
            </div>
          </div>

          {/* Overhead Breakdown */}
          {(() => {
            const overheadFields = [
              { key: 'overhead_vehicle_payments', label: 'Vehicle Payments', category: 'Vehicles' },
              { key: 'overhead_fuel', label: 'Fuel', category: 'Vehicles' },
              { key: 'overhead_vehicle_maintenance', label: 'Maintenance / Tires', category: 'Vehicles' },
              { key: 'overhead_vehicle_insurance', label: 'Vehicle Insurance', category: 'Vehicles' },
              { key: 'overhead_general_liability', label: 'General Liability', category: 'Insurance' },
              { key: 'overhead_workers_comp', label: 'Workers Comp', category: 'Insurance' },
              { key: 'overhead_professional_liability', label: 'Professional Liability (E&O)', category: 'Insurance' },
              { key: 'overhead_equipment_payments', label: 'Equipment Payments / Depreciation', category: 'Equipment' },
              { key: 'overhead_software', label: 'Software & Subscriptions', category: 'Equipment' },
              { key: 'overhead_equipment_maintenance', label: 'Calibration / Repair', category: 'Equipment' },
              { key: 'overhead_office_rent', label: 'Rent / Mortgage', category: 'Office' },
              { key: 'overhead_utilities', label: 'Utilities', category: 'Office' },
              { key: 'overhead_phone_internet', label: 'Phone / Internet', category: 'Office' },
              { key: 'overhead_office_supplies', label: 'Office Supplies', category: 'Office' },
              { key: 'overhead_owner_draw', label: "Owner's Draw / Salary", category: 'People' },
              { key: 'overhead_admin_staff', label: 'Admin / Office Staff', category: 'People' },
              { key: 'overhead_payroll_taxes', label: 'Payroll Taxes & Benefits', category: 'People' },
              { key: 'overhead_health_insurance', label: 'Health Insurance', category: 'People' },
              { key: 'overhead_accounting', label: 'Accounting / Bookkeeping', category: 'Other' },
              { key: 'overhead_legal', label: 'Legal Fees', category: 'Other' },
              { key: 'overhead_licensing', label: 'Licensing / Continuing Ed', category: 'Other' },
              { key: 'overhead_marketing', label: 'Marketing / Website', category: 'Other' },
              { key: 'overhead_other', label: 'Other', category: 'Other' },
            ];
            const categories = ['Vehicles', 'Insurance', 'Equipment', 'Office', 'People', 'Other'];
            const categoryIcons = { Vehicles: '🚛', Insurance: '🛡️', Equipment: '📡', Office: '🏢', People: '👥', Other: '📋' };
            const monthlyTotal = overheadFields.reduce((sum, f) => sum + (parseFloat(settings[f.key]) || 0), 0);
            const annualTotal = monthlyTotal * 12;
            const billableHours = settings.overhead_annual_billable_hours || 4000;
            const calculatedRate = billableHours > 0 ? annualTotal / billableHours : 0;

            return (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Overhead Breakdown</h3>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
                  Enter your monthly amounts for each category. The overhead rate per labor hour is auto-calculated.
                </p>

                {/* Summary Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
                  borderRadius: '10px',
                  padding: '20px',
                  color: 'white',
                  marginBottom: '20px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px'
                }}>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Monthly Overhead</div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>${monthlyTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Annual Overhead</div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>${annualTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>Rate Per Labor Hour</div>
                    <div style={{ fontSize: '24px', fontWeight: '700' }}>${calculatedRate.toFixed(2)}/hr</div>
                  </div>
                </div>

                {/* Billable Hours Input */}
                <div style={{ marginBottom: '20px', maxWidth: '300px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                    Estimated Annual Billable Hours
                  </label>
                  <input
                    type="number"
                    value={settings.overhead_annual_billable_hours || ''}
                    onChange={(e) => {
                      const hrs = parseFloat(e.target.value) || 0;
                      const newRate = hrs > 0 ? annualTotal / hrs : 0;
                      setSettings({...settings, overhead_annual_billable_hours: hrs, overhead_rate_per_hour: Math.round(newRate * 100) / 100});
                    }}
                    placeholder="4000"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                    Total hours your crew bills across all jobs per year
                  </p>
                </div>

                {/* Category Sections */}
                {categories.map(cat => {
                  const catFields = overheadFields.filter(f => f.category === cat);
                  const catTotal = catFields.reduce((sum, f) => sum + (parseFloat(settings[f.key]) || 0), 0);
                  return (
                    <div key={cat} style={{
                      marginBottom: '16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: '#f9fafb',
                        borderBottom: '1px solid #e5e7eb'
                      }}>
                        <span style={{ fontWeight: '600', color: '#374151' }}>
                          {categoryIcons[cat]} {cat}
                        </span>
                        <span style={{ fontWeight: '600', color: '#1e40af' }}>
                          ${catTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}/mo
                        </span>
                      </div>
                      <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                          {catFields.map(field => (
                            <div key={field.key}>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px', color: '#4b5563' }}>
                                {field.label}
                              </label>
                              <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '14px' }}>$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={settings[field.key] || ''}
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    const newSettings = {...settings, [field.key]: val};
                                    const newMonthly = overheadFields.reduce((sum, f) => sum + (f.key === field.key ? val : (parseFloat(settings[f.key]) || 0)), 0);
                                    const hrs = newSettings.overhead_annual_billable_hours || 4000;
                                    newSettings.overhead_rate_per_hour = hrs > 0 ? Math.round((newMonthly * 12 / hrs) * 100) / 100 : 0;
                                    setSettings(newSettings);
                                  }}
                                  placeholder="0.00"
                                  style={{
                                    width: '100%',
                                    padding: '8px 10px 8px 24px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '14px'
                                  }}
                                />
                              </div>
                              {field.key === 'overhead_other' && (
                                <input
                                  type="text"
                                  value={settings.overhead_other_note || ''}
                                  onChange={(e) => setSettings({...settings, overhead_other_note: e.target.value})}
                                  placeholder="Description..."
                                  style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    marginTop: '4px'
                                  }}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Material Costs */}
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Material Costs</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Rebar Cost ($/each)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.rebar_cost || ''}
                  onChange={(e) => setSettings({...settings, rebar_cost: parseFloat(e.target.value) || 0})}
                  placeholder="2.00"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: '#374151' }}>
                  Pipe/Cap Cost ($/each)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={settings.pipe_cost || ''}
                  onChange={(e) => setSettings({...settings, pipe_cost: parseFloat(e.target.value) || 0})}
                  placeholder="3.00"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: '40px' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CompanySettings;